<template lang="pug">
.b-file(:title="nameWithoutExtension" :class="{ error: !!error, process, hasThumbnail, sm, lg, xl }")
  ._remove(v-if="removable" @click="$emit('remove')")
  ._tile(@click="onTileClick")
    ._menu
      span(v-if="xl && zoomable" ) Крупные фотографии
      BaseSwitch(v-if="xl && zoomable" :value="xl" @change="$emit('zoom', $event)" @click.native.stop)
      EllipsisMenu(:items="menuItems" light vertical @click.native.stop)
    img._image(v-if="hasThumbnail" :src="thumbnail")
    ._type(:class="{ hasThumbnail }" :style="{ background: color }") {{ extension }}
    ._error(v-if="error")
      Icon(name="attention")
      BaseTooltipDropdown._error-tooltip(position="bottom") {{ error }}
    ._process(v-if="process")
      ._progress(:style="{ top: (100 - progress) + '%' }")
      Icon(:name="process" pointer)
    ._select(v-if="selectable" :class="{ selected }")
    ._size(v-if="!process && file.size") {{ size }}
  ._name {{ nameWithoutExtension }}
  ._date {{ createdAt }}
  a._download(v-if="!process" :href="file.url" download) Скачать
    Icon(name="download" pointer)
</template>

<script>
import fileTypes from '@/utils/fileTypes';
import utils from '@/utils/common';
import BaseTooltipDropdown from '@/components/BaseTooltipDropdown.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import BaseSwitch from '@/components/BaseSwitch.vue';

export default {
  name: 'FileTile',
  components: { BaseSwitch, EllipsisMenu, BaseTooltipDropdown },
  props: {
    file: {
      type: [Object, File],
      required: true,
    },
    progress: Number,
    process: {
      type: String,
      validator(value) {
        return [
          'deleting',
          'uploading',
          'waiting',
        ].indexOf(value) !== -1;
      },
    },
    error: String,
    removable: Boolean,
    selectable: Boolean,
    selected: Boolean,
    sm: Boolean,
    lg: Boolean,
    xl: Boolean,
    zoomable: Boolean,
  },
  computed: {
    extension() { return this.file.extension; },
    // info() { return fileTypes[this.extension] ?? {}; },
    info() {
      if (this.extension === 'mov' && this.$store.state.auth?.user?.customSettings?.settings?.movPreviewEnabled) {
        return { hasPreview: true, type: 'video' };
      }

      return fileTypes[this.extension] ?? {};
    },
    color() { return this.info?.color ?? '#788CA1'; },
    hasThumbnail() { return this.info.hasThumbnail; },
    hasPreview() { return this.info.hasPreview; },
    isBlank() { return this.info.blank; },
    createdAt() { return this.file.createdAt && utils.convertDateToString(new Date(this.file.createdAt), 'short'); },
    nameWithoutExtension() { return this.file.name.replace(/\.[^/.]+$/, ''); },
    size() { return utils.storageSizeCounter(this.file.size); },
    thumbnail() { return this.xl ? this.file.url : this.file.thumbnails.preview; },
    menuItems() {
      const share = () => {
        navigator.clipboard.writeText(`${document.location.origin}${this.file.url}`);
        this.$push({ text: 'Ссылка скопирована в буфер обмена' });
      };
      const items = [
        { text: 'Поделиться', icon: 'share', handler: share },
      ];

      if (this.zoomable && this.hasThumbnail) {
        items.push(
          this.xl
            ? { text: 'Не показывать крупно', icon: 'zoom-out', handler: () => { this.$emit('zoom', false); } }
            : { text: 'Показать крупно', icon: 'zoom-in', handler: () => { this.$emit('zoom', true); } },
        );
      }

      return items;
    },
  },
  methods: {
    onTileClick() {
      if (this.selectable) {
        this.$emit('toggle');
      } else if (this.hasPreview) {
        this.$emit('preview');
      } else if (this.isBlank) {
        window.open(window.location.origin + this.file.url, '_blank');
      }
    },
  },
};
</script>

<style scoped lang="scss">
  .file {
    cursor: pointer;
    position: relative;

    &__tile {
      position: relative;
      padding-bottom: 100%;
      margin-bottom: 10px;
    }

    &.sm {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    &.sm &__tile {
      width: 48px;
      height: 48px;
      padding-bottom: 0;
      border-radius: 10px;
    }

    &.sm &__type {
      font-size: 16px;
      line-height: 20px;
      border-radius: 10px;

      &.hasThumbnail {
        font-size: 10px;
        line-height: 13px;
        border-radius: 4px;
      }
    }

    &.lg &__image {
      border-radius: 30px;
    }

    &.lg &__type {
      font-size: 48px;
      line-height: 20px;
      border-radius: 30px;

      &.hasThumbnail {
        font-size: 15px;
        line-height: 28px;
        padding: 0 15px;
        border-radius: 4px;
      }
    }

    &.xl {
      font-size: 48px;
      line-height: 20px;
      border-radius: 30px;
    }

    &.xl &__tile {
      width: 100%;
      height: auto;
      padding: 0;
      border-radius: 16px;
    }

    &.xl &__image {
      border-radius: 16px;
      position: static;
    }

    &.xl &__type {
      font-size: 48px;
      line-height: 20px;
      border-radius: 30px;

      &.hasThumbnail {
        font-size: 15px;
        line-height: 28px;
        padding: 0 15px;
        border-radius: 4px;
        top: -15px;
      }
    }

    &__menu {
      z-index: 5;
      position: absolute;
      top: 10px;
      right: 0;
      display: none;
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0.01em;
      align-items: center;
      gap: 20px;
      color: #fff;
    }

    &.xl &__menu {
      top: 24px;
      right: 12px;
    }

    &:hover &__menu {
      display: flex;
    }

    &__type {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: bold;
      font-size: 24px;
      line-height: 30px;
      letter-spacing: 0.01em;
      color: #FFFFFF;
      border-radius: 10px;
      text-transform: uppercase;

      &.hasThumbnail {
        font-size: 12px;
        line-height: 15px;
        letter-spacing: 0.01em;
        color: #000000;
        top: -7px;
        right: auto;
        bottom: auto;
        padding: 2px 7px;
        border-radius: 4px;
        z-index: 1;
      }
    }

    &__image {
      border-radius: 10px;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &__size {
      display: none;
      background: rgba(36, 36, 36, .7);
      width: 100%;
      height: 100%;
      border-radius: 10px;
    }

    &:hover &__size {
      display: flex;
      position: absolute;
      color: #FFF;
      justify-content: center;
      align-items: center;
      font-size: 16px;
      letter-spacing: 0.16px;
    }

    &.sm:hover &__size {
      font-size: 10px;
    }

    &.lg:hover &__size {
      font-size: 26px;
      border-radius: 30px;
    }

    &.xl:hover &__size {
      font-size: 36px;
      border-radius: 16px;
      top: 0;
    }

    &:hover &__type:not(.hasThumbnail) {
      font-size: 0;
    }

    &__name {
      font-size: 12px;
      line-height: 15px;
      letter-spacing: 0.01em;
      color: var(--color-dark-gray);
      margin-bottom: 2px;
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__date {
      height: 13px;
      font-size: 10px;
      line-height: 13px;
      letter-spacing: 0.01em;
      color: var(--color-dark-gray);
    }

    &__download {
      display: none;
      font-weight: bold;
      font-size: 12px;
      line-height: 20px;
      height: 30px;
      align-items: flex-start;
      justify-content: center;
      gap: 7px;
      color: #92A0B0;
    }

    &__process {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #F8F9FC;
      border-radius: 10px;
      overflow: hidden;

      svg {
        width: 30px;
        height: 30px;
        z-index: 20;
      }
    }

    &__progress {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      background: #DFE8FF;
    }

    &__error {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #F94242;
      border-radius: 10px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &__error-tooltip {
      max-width: 200px;
      width: max-content;
      text-align: center;
    }

    &:hover  &__error-tooltip {
      opacity: 1;
      visibility: visible;
    }

    &__remove {
      display: none;
      position: absolute;
      z-index: 10;
      top: -8px;
      right: -8px;
      width: 20px;
      height: 20px;
      border-radius: 100%;
      background: var(--color-super-light);

      &:before, &:after {
        position: absolute;
        top: 50%;
        left: calc(50% - 5px);
        display: block;
        content: '';
        height: 1px;
        width: 10px;

        background: #C4C4C4;
        transform: rotate(-45deg);
      }

      &:before { transform: rotate(45deg); }
      &:after { transform: rotate(-45deg); }
    }

    &:hover &__remove {
      display: block;
    }

    &__select {
      position: absolute;
      right: 4px;
      bottom: 4px;
      width: 18px;
      height: 18px;
      border-radius: 100%;
      background: #FEFFFF;
      z-index: 1;

      &.selected {
        width: 20px;
        height: 20px;
        background: url(../assets/img/svg/checked-true.svg) center / contain;
      }
    }

    &:not(.error):not(.process):hover &__name,
    &:not(.error):not(.process):hover &__date {
      display: none;
    }

    &:not(.error):not(.process):hover &__download {
      display: flex;
    }
  }
</style>
